import { useFiatCurrencies } from '@capsicum/common';
import { useConversionRatesQuery, useMainCurrenciesQuery, useNCWBalance } from '@capsicum/socios-core';
import { useMemo } from 'react';

export type UseWCPledgeRatesReturn = {
	/** The user's current CHZ (Chiliz) balance. */
	chzBalance?: number;
	/** Indicates if any of the underlying async operations are still loading. */
	isLoading: boolean;
	/** The equivalent balance in USD, derived from CHZ * usdRate. */
	usdBalance?: number;
	/** The latest CHZ → USD conversion rate. */
	usdRate?: number;
	/** The USD → CHZ conversion rate (value of 1 USD in CHZ). */
	usdToChzRate?: number;
};

/**
 * Hook that provides CHZ and USD balance information along with conversion rates.
 *
 * It fetches:
 *  - The user's current CHZ (Chiliz) balance from the wallet.
 *  - The latest conversion rate from CHZ → USD.
 *  - The computed USD equivalent of the user's CHZ balance.
 *
 * It also exposes a unified `isLoading` flag to indicate when any of the underlying
 * asynchronous queries are still pending.
 *
 * @returns {UseWCPledgeRatesReturn} Object containing balance data, conversion rate, and loading state.
 **/
export const useWCPledgeRates = (): UseWCPledgeRatesReturn => {
	const { chilizBalance, isPending: isNCWBalanceLoading } = useNCWBalance();
	const { data: currencies, isPending: isCurrenciesLoading } = useMainCurrenciesQuery();
	const { data: fiatCurrencies, isPending: isFiatCurrenciesLoading } = useFiatCurrencies();

	// Derive currency IDs safely
	const chzCurrencyId = useMemo(() => currencies?.defaultCurrency?.id ?? '', [currencies]);
	const usdCurrencyId = useMemo(
		() => fiatCurrencies?.find(c => c.currency.shortCode === 'USD')?.currency.id ?? '',
		[fiatCurrencies]
	);

	// Prepare conversion query parameters
	const conversionRatesParams = useMemo(
		() => (chzCurrencyId && usdCurrencyId ? [{ from: chzCurrencyId, to: usdCurrencyId }] : []),
		[chzCurrencyId, usdCurrencyId]
	);

	const { data: conversionRatesUSD, isPending: isConversionRatesLoading } =
		useConversionRatesQuery(conversionRatesParams);

	// Extract balances and rate
	const chzBalance = chilizBalance?.usableAmount ?? 0;
	const usdRate = Number(conversionRatesUSD?.[0]?.rate ?? 0);
	const usdToChzRate = useMemo(() => (usdRate ? Number((1 / usdRate).toFixed(2)) : undefined), [usdRate]);

	// Compute USD balance
	const usdBalance = useMemo(() => chzBalance * usdRate, [chzBalance, usdRate]);

	const isLoading = isNCWBalanceLoading || isCurrenciesLoading || isFiatCurrenciesLoading || isConversionRatesLoading;

	return {
		chzBalance,
		isLoading,
		usdBalance,
		usdRate,
		usdToChzRate
	};
};
